// SPDX-License-Identifier: Apache-2.0

// scene.ts — three.js scene/camera/renderer construction for VaultOrbit3D.
//
// Pulled out of the legacy 1543-line vault-orbit.tsx (the React @r3f version)
// so the imperative Solid build can stay narrow and readable. The lighting
// rig matches v1's Scene() exactly: an ambient lift, a hot cyan point light
// at origin (the "brain core glow"), plus two cool/warm fills off-axis.
//
// `Stars` (v1 used drei's <Stars/>) is hand-rolled as a Points cloud so we
// stay on stock three.js — no drei needed for the v2 port.

import * as THREE from "three";

export interface OrbitSceneSetup {
  renderer: THREE.WebGLRenderer;
  scene: THREE.Scene;
  camera: THREE.PerspectiveCamera;
  starField: THREE.Points;
  ambient: THREE.AmbientLight;
  cyanSun: THREE.PointLight;
  violetFill: THREE.PointLight;
  amberFill: THREE.PointLight;
}

export function createOrbitScene(canvas: HTMLCanvasElement): OrbitSceneSetup {
  const renderer = new THREE.WebGLRenderer({
    canvas,
    antialias: false, // postprocessing handles AA via mipmap blur on bloom
    alpha: true,
    powerPreference: "high-performance",
    stencil: false,
    depth: true,
  });
  renderer.setPixelRatio(Math.min(window.devicePixelRatio, 1.4));
  renderer.outputColorSpace = THREE.SRGBColorSpace;
  renderer.toneMapping = THREE.ACESFilmicToneMapping;
  renderer.toneMappingExposure = 1.0;

  const scene = new THREE.Scene();
  scene.background = null; // CSS gradient drives the dark backdrop

  const camera = new THREE.PerspectiveCamera(50, 1, 0.1, 200);
  camera.position.set(0, 1.6, 7.5);
  camera.lookAt(0, 0, 0);

  // ── Lighting (mirrors v1 vault-orbit.tsx:1373-1376) ────────────────────────
  const ambient = new THREE.AmbientLight(0xffffff, 0.18);
  scene.add(ambient);

  const cyanSun = new THREE.PointLight(0x22d3ee, 4.5, 11, 1.6);
  cyanSun.position.set(0, 0, 0);
  scene.add(cyanSun);

  const violetFill = new THREE.PointLight(0xa78bfa, 0.65);
  violetFill.position.set(5, 3, 4);
  scene.add(violetFill);

  const amberFill = new THREE.PointLight(0xfbbf24, 0.45);
  amberFill.position.set(-5, -2, -3);
  scene.add(amberFill);

  // ── Stars (hand-rolled — drei's <Stars/> isn't available without r3f) ──────
  // 320 stars, fade-tinted, large enough to blur into a soft glow under bloom.
  const starGeom = new THREE.BufferGeometry();
  const STAR_COUNT = 320;
  const starPositions = new Float32Array(STAR_COUNT * 3);
  const starColors = new Float32Array(STAR_COUNT * 3);
  for (let i = 0; i < STAR_COUNT; i++) {
    // Sphere of radius 60–110, density biased toward the equator
    const r = 60 + Math.random() * 50;
    const theta = Math.random() * Math.PI * 2;
    const phi = Math.acos(2 * Math.random() - 1);
    starPositions[i * 3 + 0] = r * Math.sin(phi) * Math.cos(theta);
    starPositions[i * 3 + 1] = r * Math.sin(phi) * Math.sin(theta) * 0.55; // squash
    starPositions[i * 3 + 2] = r * Math.cos(phi);
    // Cool blueish tint with random brightness
    const b = 0.35 + Math.random() * 0.65;
    starColors[i * 3 + 0] = b * 0.85;
    starColors[i * 3 + 1] = b * 0.95;
    starColors[i * 3 + 2] = b;
  }
  starGeom.setAttribute("position", new THREE.BufferAttribute(starPositions, 3));
  starGeom.setAttribute("color", new THREE.BufferAttribute(starColors, 3));
  const starMat = new THREE.PointsMaterial({
    size: 0.42,
    sizeAttenuation: true,
    vertexColors: true,
    transparent: true,
    opacity: 0.85,
    depthWrite: false,
    blending: THREE.AdditiveBlending,
    toneMapped: false,
  });
  const starField = new THREE.Points(starGeom, starMat);
  starField.frustumCulled = false;
  starField.renderOrder = -10;
  // Disable raycasting on stars — they're decoration, not interactive.
  starField.raycast = () => undefined;
  scene.add(starField);

  return { renderer, scene, camera, starField, ambient, cyanSun, violetFill, amberFill };
}

export function disposeOrbitScene(setup: OrbitSceneSetup): void {
  setup.starField.geometry.dispose();
  (setup.starField.material as THREE.Material).dispose();
  setup.renderer.dispose();
}
